import { zodResolver } from '@hookform/resolvers/zod';
import { createSiteRequest, type SiteDto, type z } from '@zenarty/shared';
import { Building2, MapPin, Pencil, Plus, Power } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { api } from '../api';
import { useAuth } from '../auth';
import { EntityHistory } from '../components/audit';
import {
  Badge,
  Button,
  EmptyState,
  Field,
  KeyValue,
  PageHeader,
  StatusBadge,
} from '../components/kit';
import { ConfirmDialog, Dialog, Drawer } from '../components/overlays';
import { DataTable, type Column } from '../components/table';
import { formatDate, formatDateTime } from '../lib/format';
import { useAction, useClients, useSites } from '../lib/queries';

export const US_TIMEZONES = [
  ['America/New_York', 'Eastern'],
  ['America/Chicago', 'Central'],
  ['America/Denver', 'Mountain'],
  ['America/Phoenix', 'Arizona'],
  ['America/Los_Angeles', 'Pacific'],
  ['America/Anchorage', 'Alaska'],
  ['Pacific/Honolulu', 'Hawaii'],
] as const;

const US_STATES =
  'AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY'.split(
    ' ',
  );

const tzLabel = (tz: string) => US_TIMEZONES.find(([id]) => id === tz)?.[1] ?? tz;
const place = (s: SiteDto) => (s.address ? `${s.address.city}, ${s.address.state}` : null);

export function SitesPage() {
  const { can } = useAuth();
  const sites = useSites();
  const clients = useClients(can('client:read'));
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<SiteDto | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const clientCount = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of clients.data?.items ?? [])
      for (const s of c.siteIds) m.set(s, (m.get(s) ?? 0) + 1);
    return m;
  }, [clients.data]);

  const columns: Column<SiteDto>[] = [
    {
      id: 'name',
      header: 'Site',
      sortValue: (s) => s.name,
      cell: (s) => (
        <div className="row" style={{ flexWrap: 'nowrap' }}>
          <span className="icon-tile accent">
            <Building2 />
          </span>
          <div>
            <div className="primary-cell">{s.name}</div>
            <span className="code-pill">{s.code}</span>
          </div>
        </div>
      ),
    },
    {
      id: 'location',
      header: 'Location',
      sortValue: (s) => place(s) ?? '',
      cell: (s) => place(s) ?? <span className="faint">Not set</span>,
    },
    {
      id: 'tz',
      header: 'Time zone',
      sortValue: (s) => s.timezone,
      cell: (s) => <span title={s.timezone}>{tzLabel(s.timezone)}</span>,
    },
    ...(can('client:read')
      ? [
          {
            id: 'clients',
            header: 'Clients stored',
            align: 'right' as const,
            sortValue: (s: SiteDto) => clientCount.get(s.id) ?? 0,
            cell: (s: SiteDto) => clientCount.get(s.id) ?? 0,
          },
        ]
      : []),
    {
      id: 'status',
      header: 'Status',
      sortValue: (s) => s.status,
      cell: (s) => <StatusBadge status={s.status} />,
    },
    {
      id: 'created',
      header: 'Added',
      sortValue: (s) => s.createdAt,
      cell: (s) => <span className="muted">{formatDate(s.createdAt)}</span>,
    },
  ];

  const open = sites.data?.items.find((s) => s.id === openId) ?? null;
  return (
    <>
      <PageHeader
        title="Sites"
        description="Warehouses and distribution centres you operate. Each site keeps its own time zone for cut-offs and reports."
        actions={
          can('site:manage') ? (
            <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
              Add site
            </Button>
          ) : null
        }
      />
      <DataTable
        columns={columns}
        rows={sites.data?.items ?? []}
        rowKey={(s) => s.id}
        loading={sites.isLoading}
        searchPlaceholder="Search sites by name, code or city"
        searchText={(s) => `${s.name} ${s.code} ${place(s) ?? ''}`}
        onRowClick={(s) => setOpenId(s.id)}
        initialSort={{ id: 'name' }}
        empty={
          <EmptyState
            icon={Building2}
            title="No sites yet"
            action={
              can('site:manage') ? (
                <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
                  Add your first site
                </Button>
              ) : null
            }
          >
            Add each warehouse you run. Inventory, receiving and shipping are tracked per site.
          </EmptyState>
        }
      />
      {creating ? <SiteDialog onClose={() => setCreating(false)} /> : null}
      {editing ? <SiteDialog site={editing} onClose={() => setEditing(null)} /> : null}
      {open ? (
        <SiteDrawer site={open} onEdit={() => setEditing(open)} onClose={() => setOpenId(null)} />
      ) : null}
    </>
  );
}

function SiteDrawer({
  site,
  onEdit,
  onClose,
}: {
  site: SiteDto;
  onEdit: () => void;
  onClose: () => void;
}) {
  const { can } = useAuth();
  const clients = useClients(can('client:read'));
  const [confirm, setConfirm] = useState(false);
  const toggle = useAction(
    () =>
      api(`/sites/${site.id}`, {
        method: 'PATCH',
        body: { status: site.status === 'active' ? 'inactive' : 'active' },
      }),
    {
      invalidate: [['sites']],
      success: site.status === 'active' ? 'Site deactivated' : 'Site activated',
      onSuccess: () => setConfirm(false),
    },
  );
  const stored = (clients.data?.items ?? []).filter((c) => c.siteIds.includes(site.id));
  const a = site.address;
  return (
    <Drawer
      title={site.name}
      subtitle={<span className="code-pill">{site.code}</span>}
      icon={
        <span className="icon-tile accent">
          <Building2 />
        </span>
      }
      onClose={onClose}
      footer={
        can('site:manage') ? (
          <>
            <Button icon={Pencil} onClick={onEdit}>
              Edit
            </Button>
            <Button
              variant={site.status === 'active' ? 'danger' : 'default'}
              icon={Power}
              onClick={() => setConfirm(true)}
            >
              {site.status === 'active' ? 'Deactivate' : 'Activate'}
            </Button>
          </>
        ) : null
      }
    >
      <section>
        <div className="section-title">Details</div>
        <KeyValue
          items={[
            ['Status', <StatusBadge key="s" status={site.status} />],
            ['Time zone', `${tzLabel(site.timezone)} (${site.timezone})`],
            [
              'Address',
              a ? (
                <span key="a">
                  <MapPin size={13} className="muted" /> {a.line1}
                  {a.line2 ? `, ${a.line2}` : ''}, {a.city}, {a.state} {a.postalCode}
                </span>
              ) : null,
            ],
            ['Added', formatDateTime(site.createdAt)],
            ['Last changed', formatDateTime(site.updatedAt)],
          ]}
        />
      </section>
      {can('client:read') ? (
        <section>
          <div className="section-title">Clients stored here ({stored.length})</div>
          {stored.length ? (
            <div className="row">
              {stored.map((c) => (
                <Badge key={c.id} tone="accent">
                  {c.name}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="muted">No clients assigned to this site yet.</p>
          )}
        </section>
      ) : null}
      {can('audit:read') ? (
        <section>
          <div className="section-title">History</div>
          <EntityHistory entityId={site.id} />
        </section>
      ) : null}
      {confirm ? (
        <ConfirmDialog
          title={site.status === 'active' ? `Deactivate ${site.name}?` : `Activate ${site.name}?`}
          confirmLabel={site.status === 'active' ? 'Deactivate' : 'Activate'}
          danger={site.status === 'active'}
          pending={toggle.isPending}
          onConfirm={() => toggle.mutate(undefined)}
          onClose={() => setConfirm(false)}
        >
          {site.status === 'active'
            ? 'The site stays in history and reports but no new work can be planned there.'
            : 'The site becomes available for new work again.'}
        </ConfirmDialog>
      ) : null}
    </Drawer>
  );
}

type SiteForm = z.input<typeof createSiteRequest>;

function SiteDialog({ site, onClose }: { site?: SiteDto; onClose: () => void }) {
  const form = useForm<SiteForm, unknown, z.output<typeof createSiteRequest>>({
    resolver: zodResolver(createSiteRequest),
    shouldUnregister: true,
    defaultValues: site
      ? {
          code: site.code,
          name: site.name,
          timezone: site.timezone,
          address: site.address ?? undefined,
        }
      : { timezone: 'America/Chicago' },
  });
  const [withAddress, setWithAddress] = useState(Boolean(site?.address));
  const save = useAction(
    (v: z.output<typeof createSiteRequest>) => {
      const body = { ...v, address: withAddress ? v.address : undefined };
      if (!site) return api<SiteDto>('/sites', { method: 'POST', body });
      const { code: _code, ...patch } = body;
      return api<SiteDto>(`/sites/${site.id}`, {
        method: 'PATCH',
        body: { ...patch, address: withAddress ? patch.address : null },
      });
    },
    { invalidate: [['sites']], success: site ? 'Site updated' : 'Site added', onSuccess: onClose },
  );
  const e = form.formState.errors;
  return (
    <Dialog
      title={site ? `Edit ${site.name}` : 'Add a site'}
      description={
        site
          ? 'The site code cannot change once created.'
          : 'A warehouse or distribution centre you operate.'
      }
      onClose={onClose}
      onSubmit={(ev) => void form.handleSubmit((v) => save.mutate(v))(ev)}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={save.isPending}>
            {site ? 'Save changes' : 'Add site'}
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Site name" error={e.name?.message}>
          <input className="input" autoFocus {...form.register('name')} placeholder="Phoenix DC" />
        </Field>
        <Field
          label="Site code"
          error={e.code?.message}
          hint="Short unique code used on labels and reports"
        >
          <input
            className="input mono"
            disabled={Boolean(site)}
            {...form.register('code', { setValueAs: (v: string) => v.toUpperCase() })}
            placeholder="PHX1"
          />
        </Field>
        <Field
          label="Time zone"
          error={e.timezone?.message}
          hint="Cut-offs, shifts and daily reports follow this zone"
        >
          <select className="select" {...form.register('timezone')}>
            {US_TIMEZONES.map(([id, label]) => (
              <option key={id} value={id}>
                {label} — {id}
              </option>
            ))}
          </select>
        </Field>
        <div className="field">
          <span className="field-label">Address</span>
          <label className="check">
            <input
              type="checkbox"
              checked={withAddress}
              onChange={(ev) => setWithAddress(ev.target.checked)}
            />{' '}
            Add a street address
          </label>
        </div>
        {withAddress ? (
          <>
            <Field label="Street" error={e.address?.line1?.message} className="span-2">
              <input
                className="input"
                {...form.register('address.line1')}
                placeholder="4400 W Logistics Way"
              />
            </Field>
            <Field label="City" error={e.address?.city?.message}>
              <input className="input" {...form.register('address.city')} />
            </Field>
            <div className="form-grid" style={{ gap: 12 }}>
              <Field label="State" error={e.address?.state?.message}>
                <select className="select" {...form.register('address.state')}>
                  <option value="">—</option>
                  {US_STATES.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </Field>
              <Field label="ZIP" error={e.address?.postalCode?.message}>
                <input
                  className="input"
                  inputMode="numeric"
                  {...form.register('address.postalCode')}
                />
              </Field>
            </div>
          </>
        ) : null}
      </div>
    </Dialog>
  );
}
